import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Car, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { UkPhoneInput, isValidUkMobile } from "@/components/UkPhoneInput";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!isValidUkMobile(form.phone)) { setError("Please enter a valid UK mobile number"); return; }
    setLoading(true); setError("");
    try {
      await register({ ...form, role: "customer" });
      toast.success("Account created!");
      navigate("/dashboard");
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || "Registration failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 py-12">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-8">
        <Link to="/" className="mb-6 flex items-center gap-2"><span className="flex h-9 w-9 items-center justify-center rounded-lg bg-navy text-white"><Car size={18} /></span><span className="font-heading text-lg font-bold text-navy">SmartFares<span className="text-brand">4U</span></span></Link>
        <h1 className="font-heading text-2xl font-bold text-navy">Create your account</h1>
        <p className="mt-1 text-sm text-slate-500">Book faster and manage all your journeys.</p>
        <div className="mt-6 space-y-4">
          <div><Label>Full Name</Label><Input value={form.name} onChange={set("name")} data-testid="reg-name" required /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="reg-email" required /></div>
          <div><Label>UK Mobile</Label><UkPhoneInput value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} invalid={!!form.phone && !isValidUkMobile(form.phone)} data-testid="reg-phone" required /></div>
          <div><Label>Password</Label><Input type="password" value={form.password} onChange={set("password")} data-testid="reg-password" required minLength={6} /></div>
          {error && <p className="text-sm text-red-600" data-testid="reg-error">{error}</p>}
          <Button type="submit" className="w-full bg-brand hover:bg-brand/90" disabled={loading} data-testid="reg-submit">{loading ? <Loader2 size={16} className="animate-spin" /> : "Sign Up"}</Button>
        </div>
        <p className="mt-4 text-center text-sm text-slate-500">Already have an account? <Link to="/login" className="font-semibold text-brand">Log in</Link></p>
      </form>
    </div>
  );
}
